import React, { FC } from "react";
import { Button, DatePicker, Input, Select } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ROLE_TYPES } from "~/constans/roles";
import { useGetDealersForSelect } from "~/entity/Dealers/hooks/useGetDealersForSelect";
import { useGetPartnersForSelect } from "~/entity/Partners/hooks/useGetPartnersForSelect";
import { checkRole } from "~/hooks/checkRole";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { ILicenseManagementUrlParams } from "~/pages/LicenseManagement/model/licenseManagementUrlParams";
import {
  getSelectDefaultByValue,
  parseDate,
  toDateString,
} from "~/shared/lib/filterHelpers";

import s from "~/pages/Directory/shared/DirectoryFilter.less";

interface ISelectValue {
  value: string | null;
  label: string;
}

interface ILicenseFilterForm {
  dealer?: ISelectValue | null;
  partner?: ISelectValue | null;
  branchCode?: string;
  inn?: string;
  year?: ISelectValue | null;
  actionType?: ISelectValue | null;
  status?: ISelectValue | null;
  decision?: ISelectValue | null;
  createdFrom?: Date | null;
  createdTo?: Date | null;
  updatedFrom?: Date | null;
  updatedTo?: Date | null;
}

const AdminLicenseManagementFilter: FC = () => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const isAdmin = checkRole() === ROLE_TYPES.ADMIN.name;
  const {
    dealerId,
    dealerName,
    organizationId,
    partnerName,
    branchCode,
    inn,
    year,
    actionType,
    status,
    decision,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
  } = useURLParamsGetter<ILicenseManagementUrlParams>();

  const {
    options: dealerOptions,
    loading: dealersLoading,
    onSearch: onDealerSearch,
    onScrollBottom: onDealerScrollBottom,
    onOpenChange: onDealerOpenChange,
  } = useGetDealersForSelect();

  const {
    options: partnerOptions,
    loading: partnersLoading,
    onSearch: onPartnerSearch,
    onOpenChange: onPartnerOpenChange,
  } = useGetPartnersForSelect();

  const yearOptions = [
    { value: "", label: t("licenseManagement.filter.allLicenses") },
    ...["2024", "2025", "2026", "2027"].map((item) => ({
      value: item,
      label: item,
    })),
  ];

  const actionTypeOptions = [
    { value: "", label: t("licenseManagement.filter.notSelected") },
    {
      value: "additional",
      label: t("licenseManagement.actionTypes.additional"),
    },
    {
      value: "baseIssue",
      label: t("licenseManagement.actionTypes.baseIssue"),
    },
  ];

  const statusOptions = [
    { value: "", label: t("licenseManagement.filter.notSelected") },
    {
      value: "draft",
      label: t("licenseManagement.statuses.draft"),
    },
    {
      value: "pendingReview",
      label: t("licenseManagement.statuses.pendingReview"),
    },
    {
      value: "decided",
      label: t("licenseManagement.statuses.decided"),
    },
  ];

  const decisionOptions = [
    { value: "", label: t("licenseManagement.filter.notSelected") },
    {
      value: "accepted",
      label: t("licenseManagement.decisions.accepted"),
    },
    {
      value: "rejected",
      label: t("licenseManagement.decisions.rejected"),
    },
  ];

  const defaultDealer = dealerId
    ? { value: dealerId, label: dealerName || dealerId }
    : null;
  const defaultPartner = organizationId
    ? { value: organizationId, label: partnerName || organizationId }
    : null;

  const dealerOptionsWithSelected =
    defaultDealer &&
    !dealerOptions.some((option) => option.value === defaultDealer.value)
      ? [defaultDealer, ...dealerOptions]
      : dealerOptions;

  const partnerOptionsWithSelected =
    defaultPartner &&
    !partnerOptions.some((option) => option.value === defaultPartner.value)
      ? [defaultPartner, ...partnerOptions]
      : partnerOptions;

  const { control, handleSubmit, reset } = useForm<ILicenseFilterForm>({
    defaultValues: {
      dealer: defaultDealer,
      partner: defaultPartner,
      branchCode: branchCode || "",
      inn: inn || "",
      year: getSelectDefaultByValue(yearOptions, year),
      actionType: getSelectDefaultByValue(actionTypeOptions, actionType),
      status: getSelectDefaultByValue(statusOptions, status),
      decision: getSelectDefaultByValue(decisionOptions, decision),
      createdFrom: parseDate(createdFrom),
      createdTo: parseDate(createdTo),
      updatedFrom: parseDate(updatedFrom),
      updatedTo: parseDate(updatedTo),
    },
  });

  const onSubmit = (data: ILicenseFilterForm) => {
    setParams({
      dealerId: data.dealer?.value || undefined,
      dealerName: data.dealer?.label || undefined,
      organizationId: data.partner?.value || undefined,
      partnerName: data.partner?.label || undefined,
      branchCode: data.branchCode || undefined,
      inn: data.inn || undefined,
      year: data.year?.value || undefined,
      actionType: data.actionType?.value || undefined,
      status: data.status?.value || undefined,
      decision: data.decision?.value || undefined,
      createdFrom: toDateString(data.createdFrom),
      createdTo: toDateString(data.createdTo),
      updatedFrom: toDateString(data.updatedFrom),
      updatedTo: toDateString(data.updatedTo),
      page: "0",
    });
  };

  const cancelForm = () => {
    reset({
      dealer: null,
      partner: null,
      branchCode: "",
      inn: "",
      year: yearOptions[0],
      actionType: actionTypeOptions[0],
      status: statusOptions[0],
      decision: decisionOptions[0],
      createdFrom: null,
      createdTo: null,
      updatedFrom: null,
      updatedTo: null,
    });

    setParams({
      dealerId: undefined,
      dealerName: undefined,
      organizationId: undefined,
      partnerName: undefined,
      branchCode: undefined,
      inn: undefined,
      year: undefined,
      actionType: undefined,
      status: undefined,
      decision: undefined,
      createdFrom: undefined,
      createdTo: undefined,
      updatedFrom: undefined,
      updatedTo: undefined,
      page: "0",
    });
  };

  const handleDealerPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      onDealerScrollBottom();
    }
  };

  return (
    <form className={s.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.filterWrapper}>
        {isAdmin && (
          <div className={s.fieldWrapper}>
            <span className={s.label}>
              {t("licenseManagement.filter.dealer")}
            </span>
            <Controller
              name="dealer"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelInValue
                  allowClear
                  showSearch
                  filterOption={false}
                  value={value}
                  onChange={onChange}
                  options={dealerOptionsWithSelected}
                  loading={dealersLoading}
                  onSearch={onDealerSearch}
                  onOpenChange={onDealerOpenChange}
                  onPopupScroll={handleDealerPopupScroll}
                  placeholder={t("licenseManagement.filter.notSelected")}
                />
              )}
            />
          </div>
        )}
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("licenseManagement.filter.branchCode")}
          </span>
          <Controller
            name="branchCode"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("licenseManagement.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("licenseManagement.filter.inn")}</span>
          <Controller
            name="inn"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) =>
                  onChange(
                    event.target.value.replace(/[^\d]/g, "").slice(0, 12),
                  )
                }
                onBlur={onBlur}
                placeholder={t("licenseManagement.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("licenseManagement.filter.year")}</span>
          <Controller
            name="year"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={yearOptions}
              />
            )}
          />
        </div>
        {isAdmin && (
          <div className={s.fieldWrapper}>
            <span className={s.label}>
              {t("licenseManagement.filter.partner")}
            </span>
            <Controller
              name="partner"
              control={control}
              render={({ onChange, value }) => (
                <Select
                  labelInValue
                  allowClear
                  showSearch
                  filterOption={false}
                  value={value}
                  onChange={onChange}
                  options={partnerOptionsWithSelected}
                  loading={partnersLoading}
                  onSearch={onPartnerSearch}
                  onOpenChange={onPartnerOpenChange}
                  placeholder={t("licenseManagement.filter.notSelected")}
                />
              )}
            />
          </div>
        )}
      </div>

      <div className={s.filterWrapper}>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("licenseManagement.filter.actionType")}
          </span>
          <Controller
            name="actionType"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={actionTypeOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("licenseManagement.filter.status")}
          </span>
          <Controller
            name="status"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={statusOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("licenseManagement.filter.decision")}
          </span>
          <Controller
            name="decision"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={decisionOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("licenseManagement.filter.createdAt")}
          </span>
          <div className={s.rangeWrapp}>
            <Controller
              control={control}
              name="createdFrom"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("licenseManagement.filter.dateFrom")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
            <span className={s.rangeDivider}>-</span>
            <Controller
              control={control}
              name="createdTo"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("licenseManagement.filter.dateTo")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
          </div>
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("licenseManagement.filter.updatedAt")}
          </span>
          <div className={s.rangeWrapp}>
            <Controller
              control={control}
              name="updatedFrom"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("licenseManagement.filter.dateFrom")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
            <span className={s.rangeDivider}>-</span>
            <Controller
              control={control}
              name="updatedTo"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("licenseManagement.filter.dateTo")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className={s.buttons}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={cancelForm}
        >
          {t("licenseManagement.filter.reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("licenseManagement.filter.show")}
        </Button>
      </div>
    </form>
  );
};

export default AdminLicenseManagementFilter;
